import { Link } from "react-router-dom";
import { Calendar, MapPin, ChevronRight } from "lucide-react";
import { CONTAINER_CLASS } from "../../constants/layout";
import { resolveEditorialEventHref, contentEventDateLine } from "../../utils/marketingHref";
import { recordContentEventClick } from "../../utils/campaignTracking";

/**
 * Bandeau accueil — événements éditoriaux (hors merchandising campagnes).
 * Données : GET /api/homepage-marketing → events_preview
 */
export function HomeEventsPreview({ rows }) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;

  return (
    <section className="border-y border-slate-200/80 bg-gradient-to-b from-slate-50/90 to-white py-8 md:py-10" data-testid="home-events-preview">
      <div className={CONTAINER_CLASS}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#06249e]">Agenda</p>
            <h2 className="mt-1 font-heading text-xl md:text-2xl font-black text-gray-900 tracking-tight">
              Prochains rendez-vous
            </h2>
            <p className="mt-1 text-sm text-gray-600 max-w-xl">
              Salons, portes ouvertes, animations — informations pratiques, pas des promotions catalogue.
            </p>
          </div>
          <Link
            to="/evenements"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#06249e] hover:underline"
          >
            Tous les événements <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((ev) => {
            const href = resolveEditorialEventHref(ev);
            const external = href.startsWith("http");
            const dateLine = contentEventDateLine(ev);
            const onNavigate = () => recordContentEventClick(ev.id);
            return (
              <li key={ev.id}>
                {external ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onNavigate}
                    className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#06249e]/30 hover:shadow-md"
                  >
                    <EventCardInner ev={ev} dateLine={dateLine} />
                  </a>
                ) : (
                  <Link
                    to={href}
                    onClick={onNavigate}
                    className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#06249e]/30 hover:shadow-md"
                  >
                    <EventCardInner ev={ev} dateLine={dateLine} />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function EventCardInner({ ev, dateLine }) {
  const accent = ev.accent_color || "#06249e";
  return (
    <>
      <div className="flex items-start gap-3">
        {ev.image ? (
          <img src={ev.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-slate-100 shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <Calendar className="w-6 h-6 text-slate-300" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Événement</p>
          <h3 className="font-heading text-[15px] font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-[#06249e]">
            {ev.title}
          </h3>
          {dateLine ? (
            <p className="mt-1 flex items-center gap-1 text-xs text-gray-600">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              {dateLine}
            </p>
          ) : null}
          {ev.location ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 line-clamp-2">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              {ev.location}
            </p>
          ) : null}
        </div>
      </div>
      <span
        className="mt-3 inline-flex w-fit items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold text-white"
        style={{ backgroundColor: accent }}
      >
        {ev.cta_label || "En savoir plus"}
        <ChevronRight className="w-3.5 h-3.5" />
      </span>
    </>
  );
}
